'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ZoomInIcon,
  ZoomOutIcon,
  ResetIcon,
  EnterFullScreenIcon,
  DownloadIcon,
  Cross2Icon,
} from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { IconButton } from '../icon-button';
import { Stack } from '../stack';
import { Text } from '../text';

export interface ImageViewerProps extends BoxProps {
  /** Image source URL */
  src: string;
  /** Alt text for the image */
  alt?: string;
  /** Initial zoom level (1 = 100%) */
  initialZoom?: number;
  /** Minimum zoom level */
  minZoom?: number;
  /** Maximum zoom level */
  maxZoom?: number;
  /** Zoom step increment */
  zoomStep?: number;
  /** Whether to show controls */
  showControls?: boolean;
  /** Whether to enable keyboard shortcuts */
  enableKeyboard?: boolean;
  /** Callback when image is loaded */
  onLoad?: () => void;
  /** Callback when image fails to load */
  onError?: () => void;
  /** Whether to show loading state */
  loading?: boolean;
}

/**
 * ImageViewer component - displays images with zoom and pan functionality
 * Supports mouse wheel zoom, drag to pan, and keyboard controls
 */
export const ImageViewer: React.FC<ImageViewerProps> = ({
  src,
  alt = 'Image',
  initialZoom = 1,
  minZoom = 0.1,
  maxZoom = 5,
  zoomStep = 0.1,
  showControls = true,
  enableKeyboard = true,
  onLoad,
  onError,
  loading = false,
  ...boxProps
}) => {
  const [zoom, setZoom] = useState(initialZoom);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [_imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [_containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Update container size on mount and resize
  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateContainerSize();
    window.addEventListener('resize', updateContainerSize);

    return () => {
      window.removeEventListener('resize', updateContainerSize);
    };
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + zoomStep, maxZoom));
  }, [zoomStep, maxZoom]);

  const zoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - zoomStep, minZoom));
  }, [zoomStep, minZoom]);

  // Handle keyboard controls
  useEffect(() => {
    if (!enableKeyboard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
        case '_':
          zoomOut();
          break;
        case '0':
          resetZoom();
          break;
        case 'ArrowUp':
          pan(0, 50);
          break;
        case 'ArrowDown':
          pan(0, -50);
          break;
        case 'ArrowLeft':
          pan(50, 0);
          break;
        case 'ArrowRight':
          pan(-50, 0);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoom, position, enableKeyboard, zoomIn, zoomOut]);

  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageSize({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      });
    }
    setImageLoaded(true);
    setImageError(false);
    onLoad?.();
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
    onError?.();
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const pan = (deltaX: number, deltaY: number) => {
    setPosition((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
    setZoom((prev) => Math.max(minZoom, Math.min(maxZoom, prev + delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = src;
    link.download = alt || 'image';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const transform = `translate(${position.x}px, ${position.y}px) scale(${zoom})`;

  const imageStyles: React.CSSProperties = {
    transform,
    transformOrigin: 'center',
    transition: isDragging ? 'none' : 'transform 0.2s',
    maxWidth: 'none',
    maxHeight: 'none',
    display: imageLoaded ? 'block' : 'none',
    userSelect: 'none',
    pointerEvents: 'none',
  };

  return (
    <Box
      ref={containerRef}
      position="relative"
      width="100%"
      height="100%"
      overflow="hidden"
      backgroundColor="bg.canvas"
      borderRadius="lg"
      cursor={isDragging ? 'grabbing' : 'grab'}
      onWheel={handleWheel}
      {...boxProps}
    >
      {/* Loading state */}
      {(loading || (!imageLoaded && !imageError)) && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          textAlign="center"
        >
          <Text color="fg.muted">Loading image...</Text>
        </Box>
      )}

      {/* Error state */}
      {imageError && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          textAlign="center"
        >
          <Stack gap={2} alignItems="center">
            <Box color="fg.error" fontSize="2xl">
              <Cross2Icon />
            </Box>
            <Text color="fg.error">Failed to load image</Text>
          </Stack>
        </Box>
      )}

      {/* Image */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={imageStyles}
          draggable={false}
        />
      </Box>

      {/* Controls */}
      {showControls && imageLoaded && (
        <Box
          position="absolute"
          bottom={4}
          left="50%"
          transform="translateX(-50%)"
          backgroundColor="bg.surface"
          borderRadius="lg"
          padding={2}
          boxShadow="lg"
          border="1px solid"
          borderColor="border.muted"
        >
          <Stack direction="row" gap={1}>
            <IconButton
              size="sm"
              variant="ghost"
              onClick={zoomOut}
              disabled={zoom <= minZoom}
              aria-label="Zoom out"
              title="Zoom out"
              icon={<ZoomOutIcon />}
            />

            <Box
              display="flex"
              alignItems="center"
              paddingX={2}
              minWidth="80px"
              justifyContent="center"
            >
              <Text fontSize="sm" fontWeight="medium">
                {Math.round(zoom * 100)}%
              </Text>
            </Box>

            <IconButton
              size="sm"
              variant="ghost"
              onClick={zoomIn}
              disabled={zoom >= maxZoom}
              aria-label="Zoom in"
              title="Zoom in"
              icon={<ZoomInIcon />}
            />

            <Box width="1px" backgroundColor="border.muted" marginX={1} />

            <IconButton
              size="sm"
              variant="ghost"
              onClick={resetZoom}
              aria-label="Reset zoom"
              title="Reset zoom"
              icon={<ResetIcon />}
            />

            <IconButton
              size="sm"
              variant="ghost"
              onClick={handleFullscreen}
              aria-label="Fullscreen"
              title="Fullscreen"
              icon={<EnterFullScreenIcon />}
            />

            <IconButton
              size="sm"
              variant="ghost"
              onClick={handleDownload}
              aria-label="Download"
              title="Download"
              icon={<DownloadIcon />}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ImageViewer;
